import { LoadingIndicator } from "@/Icons/loadingIndicator";
import {
  TFormatNumberSuffix,
  formatNumberForTable,
} from "@/Utils/formatFunctions";
import { EmptyView } from "@/components/EmptyView/EmptyView";

import { FC, useMemo } from "react";
import "react-loading-skeleton/dist/skeleton.css";

import { Card, ICardComponentProps } from "./card";

export const NumberCard: FC<
  { numbers: INumberEntryProps[]; isLoading: boolean } & ICardComponentProps
> = ({
  numbers,
  title,
  filters,
  isLoading,
  useSearchParams,
  onFilterOptionChange,
  filterLocation,
}) => {
  const view = useMemo(() => {
    if (!numbers.length) {
      return <EmptyView type="failToFetch" />;
    }

    return numbers.map((numberEntry, index) => (
      <NumberEntry
        key={`numberCard-entry-${index}`}
        {...numberEntry}
        data={isLoading ? "-" : numberEntry.data}
      />
    ));
  }, [isLoading, numbers]);

  return (
    <Card
      title={title}
      filters={filters}
      onFilterOptionChange={onFilterOptionChange}
      useSearchParams={useSearchParams}
      filterLocation={filterLocation}
      className="xl:row-auto"
    >
      <div className="flex h-full w-full flex-wrap items-center justify-around gap-4 sm:flex-row sm:gap-6">
        {view}
      </div>
    </Card>
  );
};

export interface INumberEntryProps {
  heading: string;
  data: number | string;
  suffix?: TFormatNumberSuffix;
}

const NumberEntry: FC<INumberEntryProps> = ({ heading, data, suffix = "" }) => {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center sm:h-[119px] sm:w-auto">
      <>
        <div className="mx-2 mb-2 text-center text-sm font-medium">
          {heading}
        </div>
        {data === "-" ? (
          <div className="flex h-[52px] items-center justify-center">
            <LoadingIndicator />
          </div>
        ) : (
          <div className="font-signifier text-3xl sm:text-2xl lg:text-4xl">
            {formatNumberForTable(data, { suffix, numDecimals: 0 })}
          </div>
        )}
      </>
    </div>
  );
};
